// SEO Helper - JSON-LD Schema generators for Frome Yoga
import { useEffect } from 'react';

// Base business info
export const BUSINESS_INFO = {
  name: "Annelie Lily Yoga",
  legalName: "Seventh Sense Healing Limited",
  url: "https://annelielily.com",
  telephone: "+447540995021",
  email: "seventhsensehealing@outlook.com",
  address: {
    streetAddress: "Little Keyford Lane",
    addressLocality: "Frome",
    addressRegion: "Somerset",
    postalCode: "BA11 5BA",
    addressCountry: "GB"
  },
  geo: {
    latitude: 51.2279,
    longitude: -2.3214
  },
  founder: {
    name: "Annelie Lily",
    jobTitle: "Yoga Teacher & Holistic Therapist",
    description: "British Wheel of Yoga certified teacher since 2013"
  },
  priceRange: "££"
};

// Per-section default og:image URLs.
// If a page doesn't pass ogImage, the helper picks the most relevant one based on
// the canonical URL prefix, so social shares for /retreats/* show a retreat photo,
// /treatments/* shows the treatment room, etc. (Avoids every page sharing the same
// generic homepage image on Facebook/Twitter/LinkedIn.)
export const SECTION_OG_IMAGES = {
  default:
    "https://static.prod-images.emergentagent.com/jobs/6879cb3e-4939-42bf-8d4d-2e1f5649940c/images/9cc659edd7c1fcffa59386752773610822265b7d9c4e8ddb43c000bb3abc41e6.png",
  yogaClasses:
    "https://customer-assets.emergentagent.com/job_8928e5c6-c128-403f-9029-6531d7ff9ebf/artifacts/85ch7iny_IMG_2885.jpg",
  treatments:
    "https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/i2hmz3ig_IMG_1863.jpeg",
  retreats:
    "https://customer-assets.emergentagent.com/job_8928e5c6-c128-403f-9029-6531d7ff9ebf/artifacts/n2y8ah76_IMG_4824.jpg",
  womensCircles:
    "https://customer-assets.emergentagent.com/job_8928e5c6-c128-403f-9029-6531d7ff9ebf/artifacts/vjkiskbu_1B29DE7B-D45D-436B-961A-43BE56B9E6D6.jpg",
  about:
    "https://customer-assets.emergentagent.com/job_8928e5c6-c128-403f-9029-6531d7ff9ebf/artifacts/6fn03bja_CC8E4CF2-ED69-4714-A6ED-C6A25F7F65A7.PNG",
};

const pickDefaultOgImage = (canonical = "") => {
  if (!canonical) return SECTION_OG_IMAGES.default;
  if (canonical.includes("/yoga-classes") || canonical.includes("/areas/yoga-")) return SECTION_OG_IMAGES.yogaClasses;
  if (canonical.includes("/treatments")) return SECTION_OG_IMAGES.treatments;
  if (canonical.includes("/retreats")) return SECTION_OG_IMAGES.retreats;
  if (canonical.includes("/womens-circles")) return SECTION_OG_IMAGES.womensCircles;
  if (canonical.includes("/about")) return SECTION_OG_IMAGES.about;
  return SECTION_OG_IMAGES.default;
};

// Per-route social share card. Each canonical route has a pre-generated branded
// 1200x630 PNG committed at /og/[slug].png (see scripts/generate-og-images.py).
// Slug is the canonical path with "/" -> "-" (home = "home"). Falls back to the
// section photo if the canonical can't be parsed.
const ogImageForCanonical = (canonical = "") => {
  if (!canonical) return pickDefaultOgImage(canonical);
  try {
    const path = new URL(canonical).pathname.replace(/\/+$/, "");
    const slug = path === "" ? "home" : path.replace(/^\/+/, "").replace(/\//g, "-");
    return `${BUSINESS_INFO.url}/og/${slug}.png`;
  } catch (e) {
    return pickDefaultOgImage(canonical);
  }
};

// Update page meta tags
// Sets title, description, canonical, og:*, twitter:*, hreflang for the current route.
// Critical: every page must call this so that pre-rendering (scripts/prerender.js)
// snapshots unique per-page meta into the static HTML Google crawls.
export const usePageMeta = ({ title, description, canonical, ogImage, ogType }) => {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (selector, attr, value, createIfMissing) => {
      let el = document.querySelector(selector);
      if (!el && createIfMissing) {
        el = document.createElement("meta");
        const match = selector.match(/\[(\w+)="([^"]+)"\]/);
        if (match) el.setAttribute(match[1], match[2]);
        document.head.appendChild(el);
      }
      if (el && value) el.setAttribute(attr, value);
    };

    const resolvedImage = ogImage || ogImageForCanonical(canonical);

    if (description) setMeta('meta[name="description"]', "content", description, true);
    if (title) setMeta('meta[property="og:title"]', "content", title, true);
    if (description) setMeta('meta[property="og:description"]', "content", description, true);
    if (canonical) setMeta('meta[property="og:url"]', "content", canonical, true);
    if (resolvedImage) setMeta('meta[property="og:image"]', "content", resolvedImage, true);
    setMeta('meta[property="og:type"]', "content", ogType || "website", true);

    // Twitter card mirrors OG
    if (title) setMeta('meta[name="twitter:title"]', "content", title, true);
    if (description) setMeta('meta[name="twitter:description"]', "content", description, true);
    if (canonical) setMeta('meta[name="twitter:url"]', "content", canonical, true);
    if (resolvedImage) setMeta('meta[name="twitter:image"]', "content", resolvedImage, true);

    // Canonical link
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      if (!canonicalTag) {
        canonicalTag = document.createElement("link");
        canonicalTag.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalTag);
      }
      canonicalTag.setAttribute("href", canonical);
    }

    // Hreflang tags (en-GB primary, en, x-default — all point to this canonical)
    if (canonical) {
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
      ["en-GB", "en", "x-default"].forEach((lang) => {
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", lang);
        link.setAttribute("href", canonical);
        document.head.appendChild(link);
      });
    }

    if (typeof window !== "undefined" && window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        else window.scrollTo(0, 0);
      }, 300);
    } else {
      window.scrollTo(0, 0);
    }
  }, [title, description, canonical, ogImage, ogType]);
};

// LocalBusiness + YogaStudio schema for homepage
export const generateHomeSchema = () => ({
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "HealthAndBeautyBusiness"],
  "@id": BUSINESS_INFO.url,
  "name": BUSINESS_INFO.name,
  "alternateName": "Seventh Sense Healing",
  "legalName": BUSINESS_INFO.legalName,
  "description": "Yoga classes, holistic treatments, women's circles and retreats in Frome, Somerset. Small group classes and private sessions with British Wheel of Yoga certified teacher Annelie Lily.",
  "url": BUSINESS_INFO.url,
  "telephone": BUSINESS_INFO.telephone,
  "email": BUSINESS_INFO.email,
  "address": {
    "@type": "PostalAddress",
    ...BUSINESS_INFO.address
  },
  "geo": {
    "@type": "GeoCoordinates",
    ...BUSINESS_INFO.geo
  },
  "founder": {
    "@type": "Person",
    ...BUSINESS_INFO.founder
  },
  "priceRange": BUSINESS_INFO.priceRange,
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    "opens": "09:00",
    "closes": "21:00"
  },
  "areaServed": [
    { "@type": "City", "name": "Frome" },
    { "@type": "City", "name": "Bath" },
    { "@type": "City", "name": "Trowbridge" },
    { "@type": "City", "name": "Warminster" },
    { "@type": "City", "name": "Westbury" },
    { "@type": "City", "name": "Shepton Mallet" },
    { "@type": "City", "name": "Wells" },
    { "@type": "City", "name": "Radstock" },
    { "@type": "City", "name": "Midsomer Norton" },
    { "@type": "City", "name": "Mells" },
    { "@type": "City", "name": "Nunney" },
    { "@type": "City", "name": "Beckington" },
    { "@type": "City", "name": "Bradford-on-Avon" }
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Yoga Classes & Treatments",
    "itemListElement": [
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Hatha Yoga Classes" }},
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Yin Yoga Classes" }},
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Aerial Yoga" }},
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Private Yoga Sessions" }},
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Swedish Massage" }},
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Reiki Healing" }},
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Chakra Healing" }}
    ]
  },
  // Aggregated client reviews (from Facebook). These appear as star ratings in Google's
  // rich results. Based on the 3 verified Facebook reviews in shared/Reviews.js.
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "5.0",
    "bestRating": "5",
    "worstRating": "1",
    "ratingCount": "3",
    "reviewCount": "3"
  },
  "review": [
    {
      "@type": "Review",
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
      "author": { "@type": "Person", "name": "H from Somerset" },
      "reviewBody": "Annelie's yin yoga is really rewarding and enjoyable. I always leave her classes feeling relaxed and totally de-stressed both physically and mentally. Annelie's Women's Circles are interesting and fun, and I have made a lot of friends by going to the Circles."
    },
    {
      "@type": "Review",
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
      "author": { "@type": "Person", "name": "A from Exeter" },
      "reviewBody": "The beauty about this community is that everyone is welcomed as their individual self — however, when we come together in a group setting there is a sense of unity, mutual trust, respect and understanding."
    },
    {
      "@type": "Review",
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
      "author": { "@type": "Person", "name": "H from Exeter" },
      "reviewBody": "A truly wonderful and life changing experience. Annelie is a wonderful teacher, her knowledge is fascinating. She took care with everyone with differing abilities and has a very soothing, calming voice. 11/10 for the whole experience."
    }
  ]
});

// Service schema generator
export const generateServiceSchema = ({ name, description, serviceType, url, image }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": serviceType,
  "name": name,
  "description": description,
  "url": url,
  "image": image,
  "provider": {
    "@type": "LocalBusiness",
    "name": BUSINESS_INFO.name,
    "address": {
      "@type": "PostalAddress",
      ...BUSINESS_INFO.address
    },
    "telephone": BUSINESS_INFO.telephone,
    "email": BUSINESS_INFO.email
  },
  "areaServed": {
    "@type": "City",
    "name": "Frome",
    "containedInPlace": {
      "@type": "AdministrativeArea",
      "name": "Somerset"
    }
  }
});

// Location page schema
export const generateLocationSchema = ({ townName, description, url }) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": `Annelie Lily Yoga - Yoga Classes for ${townName} Residents`,
  "description": description,
  "url": url,
  "telephone": BUSINESS_INFO.telephone,
  "email": BUSINESS_INFO.email,
  "address": {
    "@type": "PostalAddress",
    ...BUSINESS_INFO.address
  },
  "geo": {
    "@type": "GeoCoordinates",
    ...BUSINESS_INFO.geo
  },
  "areaServed": [
    { "@type": "City", "name": townName },
    { "@type": "City", "name": "Frome" }
  ]
});

// FAQ schema generator
export const generateFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

// Person schema for About page
export const generatePersonSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "name": "Annelie Lily",
  "jobTitle": "Yoga Teacher & Holistic Therapist",
  "description": "British Wheel of Yoga certified teacher since 2013. Founder of Seventh Sense Healing Limited. Teaches Hatha yoga, Yin yoga, Aerial yoga, and offers holistic treatments including Reiki, massage, and chakra healing.",
  "worksFor": {
    "@type": "LocalBusiness",
    "name": BUSINESS_INFO.name
  },
  "knowsAbout": [
    "Hatha Yoga", "Yin Yoga", "Aerial Yoga", "Kundalini Yoga",
    "Reiki Healing", "Chakra Healing", "Swedish Massage", "Crystal Healing",
    "AuraTransformation", "Children's Yoga", "Women's Circles"
  ],
  "hasCredential": [
    { "@type": "EducationalOccupationalCredential", "credentialCategory": "British Wheel of Yoga Teacher Certification" },
    { "@type": "EducationalOccupationalCredential", "credentialCategory": "Reiki Master Practitioner" },
    { "@type": "EducationalOccupationalCredential", "credentialCategory": "One Yoga and Wellness - Aerial Yoga Teacher" },
    { "@type": "EducationalOccupationalCredential", "credentialCategory": "One Yoga and Wellness - Yin Yoga Teacher" },
    { "@type": "EducationalOccupationalCredential", "credentialCategory": "Swedish Massage Therapist" },
    { "@type": "EducationalOccupationalCredential", "credentialCategory": "Aura Mediator" }
  ]
});

// WebSite schema (brand entity, injected on the homepage).
// No SearchAction is declared because the site has no on-site search box.
export const generateWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${BUSINESS_INFO.url}/#website`,
  "name": BUSINESS_INFO.name,
  "url": BUSINESS_INFO.url,
  "inLanguage": "en-GB",
  "publisher": { "@id": `${BUSINESS_INFO.url}/#organization` }
});

// Organization schema (brand entity for knowledge panel / sitelinks).
// sameAs lists ONLY the real, verified social profiles.
export const generateOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${BUSINESS_INFO.url}/#organization`,
  "name": BUSINESS_INFO.name,
  "legalName": BUSINESS_INFO.legalName,
  "url": BUSINESS_INFO.url,
  "logo": SECTION_OG_IMAGES.default,
  "image": SECTION_OG_IMAGES.default,
  "email": BUSINESS_INFO.email,
  "telephone": BUSINESS_INFO.telephone,
  "address": {
    "@type": "PostalAddress",
    ...BUSINESS_INFO.address
  },
  "sameAs": [
    "https://www.facebook.com/seventhsensehealing",
    "https://www.instagram.com/seventh_sense_healing",
    "https://www.youtube.com/channel/UClfl6mm-QU-_3pyrXBU6B6g"
  ]
});

// Schema injection component
export const SchemaScript = ({ schema }) => {
  useEffect(() => {
    const id = `schema-${Math.random().toString(36).substr(2, 9)}`;
    const script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, [schema]);
  
  return null;
};
